import { useMemo } from "react"
import { useWidget } from "../../../providers/widget"
import { hostFontSizeCss } from "../../../common/host-scope"
import { DescriptionWidgetConfig } from "../config"

/**
 * Dark-palette overrides for the auto FIRST-PAINT path (#900) plus the runtime
 * marker. The base template always emits the LIGHT palette (so light output is
 * byte-identical); this appends the DARK palette under two selector prefixes:
 *   - `:host([data-hr-theme="dark"])` — the runtime marker (explicit dark +
 *     post-hydration auto) and the mirrored elevated-dialog host.
 *   - inside `@media (prefers-color-scheme: dark)`,
 *     `:host([data-hr-scheme="auto"]:not([data-hr-theme]))` — a server-stamped
 *     auto widget on a dark OS, painting dark AT PARSE with no JS/cookie; the
 *     `:not` drops it the instant the runtime stamps the marker at hydration.
 * Mirrors the `.dark`-class branch of the template: the container/dialog text
 * and dialog background use the shared `--foreground`/`--background` tokens
 * (flipped by styles/common.css under these same selectors), while the accent
 * is config-derived, so it comes from the DARK color set here rather than the
 * resolvedTheme-gated `colors.accent` — so it ships on every render, not just
 * once the runtime has resolved dark. `:host(...)` (not a descendant
 * `[data-hr-theme]`) is required to reach the shadow host from its own sheet.
 */
function descriptionDarkOverrides(
  prefix: string,
  dc: DescriptionWidgetConfig['darkModeColors'],
): string {
  return `
    ${prefix} .homerunner-description-container,
    ${prefix} .homerunner-description-dialog {
      color: var(--foreground) !important;
    }
    ${prefix} .homerunner-description-dialog {
      background-color: var(--background) !important;
    }
    ${dc.accent ? `
    ${prefix} .text-accent {
      color: ${dc.accent} !important;
    }
    ${prefix} .bg-accent {
      background-color: ${dc.accent} !important;
    }
    ${prefix} .border-accent {
      border-color: ${dc.accent} !important;
    }
    ` : ''}
  `
}

export function useDescriptionCss(): string {
  const { options, resolvedTheme, widgetId } = useWidget<DescriptionWidgetConfig>()

  const colors = useMemo(() => {
    return resolvedTheme === 'dark' ? options.darkModeColors : options.lightModeColors
  }, [resolvedTheme, options.lightModeColors, options.darkModeColors])

  return useMemo(() => `
    .homerunner-description-container *, .homerunner-description-dialog * {
      font-family: '${options.font.family}' !important;
    }
    .homerunner-description-container.dark, .homerunner-description-dialog.dark {
      color: var(--foreground) !important;
    }
    .homerunner-description-dialog.dark {
      background-color: var(--background) !important;
    }
    ${hostFontSizeCss(widgetId, options.font.size)}
    ${colors.accent ? `
    .text-accent {
      color: ${colors.accent} !important;
    }
    .bg-accent {
      background-color: ${colors.accent} !important;
    }
    .border-accent {
      border-color: ${colors.accent} !important;
    }
    ` : ''}
    /* Dark palette for the auto first-paint path (#900) plus the runtime
       marker. The base rules above always emit LIGHT; these override only under
       dark selectors, so light rendering is byte-identical to before. */
    ${descriptionDarkOverrides(':where(:host([data-hr-theme="dark"]))', options.darkModeColors)}
    @media (prefers-color-scheme: dark) {
      ${descriptionDarkOverrides(':where(:host([data-hr-scheme="auto"]:not([data-hr-theme])))', options.darkModeColors)}
    }
    ${options.customCss ?? ''}
  `, [widgetId, options.font.family, options.font.size, colors.accent, options.darkModeColors.accent, options.customCss])
}
